import i18n from 'i18next'
import { answerValidate } from './utils'

// Pro learners get one teacher correction per day. The writing answer and the
// speaking recording are NOT two separate work items: the backend upserts both
// onto the same story_correction document (POST /question and
// POST /story-listening/recording both look up the correction by story+member),
// so adding a recording to the story you already wrote about today costs the
// teacher nothing extra.
//
// The daily cap therefore only applies to a *different* story, and only while
// today's correction is still open — anything attached after the teacher closed
// it never gets reviewed.
export interface StorySubmissionState {
  record_exist?: boolean
  storyId?: string
  isOpen?: boolean
}

export const canRequestTeacherCorrection = (
  storySubmitted: StorySubmissionState | null | undefined,
  storyId: string | undefined,
): boolean => {
  const submittedStoryId = storySubmitted?.storyId

  // No correction submitted today: the daily slot is untouched. This is also the
  // unknown state (deep link into a story before the stories list has been
  // fetched), where we deliberately fail open — a silently missing Pro feature is
  // worse than the rare extra correction.
  if (!storySubmitted?.record_exist || !submittedStoryId) return true

  // Today's slot was spent on another story.
  if (submittedStoryId !== storyId) return false

  // Same story: only while the teacher hasn't closed the correction. `isOpen` is
  // undefined on responses from older backends, which we treat as still open.
  return storySubmitted.isOpen !== false
}

/**
 * Both `POST /question` and `POST /story-listening/recording` save the learner's
 * work unconditionally and may decline only the teacher review, flagging it with
 * `dailyCorrectionAlreadyUsed`. Treat that as a notice, never as a failure — the
 * submission itself succeeded.
 */
export const wasTeacherCorrectionDeclined = (response: any): boolean =>
  response?.dailyCorrectionAlreadyUsed === true

/** Single home for the copy so writing and speaking say the same thing. */
export const TEACHER_CORRECTION_DAILY_SLOT_USED =
  "You've already used today's teacher correction on another story. " +
  'Your work is saved — your next correction unlocks tomorrow.'

/**
 * Teachers explain a correction in a note: 【'和' can't join two sentences】.
 * Learners only see full-width 【】 as a note (CorrectionCard, the correction
 * email), so a note typed with plain [ ] is converted before it is saved.
 * Otherwise it lands in the learner's sentence as one long insertion.
 */
export const normalizeTeacherNotes = (correction: string): string =>
  correction.replace(/\[([^[\]]*)\]/g, '【$1】')

/** The corrected text without its notes, plus the notes themselves. */
export const splitTeacherNoteText = (correction: string): { body: string; notes: string[] } => {
  const notes: string[] = []
  const body = normalizeTeacherNotes(correction).replace(/【([^】]*)】/g, (_, note: string) => {
    if (note.trim()) notes.push(note.trim())
    return ''
  })
  return { body: body.trim(), notes }
}

/**
 * The corrected text follows the same rule as a learner's answer: at least 80%
 * Chinese. Notes are left out of the count, so a teacher can explain in English
 * as much as they need to.
 */
export const correctionValidate = async (correction: string): Promise<{ error: string }> => {
  const result = await answerValidate(splitTeacherNoteText(correction).body)
  // Compare the code, not the message: the message follows the interface language.
  if (result.code === 'notChinese') {
    return { error: i18n.t('common:validation.correctionNotChinese') }
  }
  return { error: result.error }
}
